import React from "react";
import { Card, Flex, Text, LoadingIndicator, ErrorMessage, Tag } from "@jsq/ds/core";
import { formatError, useReportError } from "#common/hooks/useReportError";
import { useGetPayeeForBankAccountDetailsQuery } from "#graphql/supergraphHooks";
import { clientContext } from "#utils/graphql_utils";
import { BasePaymentPrefDisplay } from "../../../PaymentPrefConsolidated/Components/BasePaymentPrefDisplay";
import { ExistingPaymentPrefDetails } from "../../../PaymentPrefConsolidated/Components/ExistingPaymentPrefDetails";
import FormattedAddress from "../../../PaymentPrefConsolidated/Components/PaymentPrefDetails/FormattedAddress";
import { PaymentPrefDisplayCardContainer } from "../../../PaymentPrefConsolidated/Components/PaymentPrefDisplayCardContainer";
import { getPaymentMethodIconType } from "../../../PaymentPrefConsolidated/Components/PaymentPrefDisplayCardContainer/utils";
import { getPayeeReviewTag } from "../../utils";
import * as styles from "./styles_vanilla.css";
import { PayeeDisplayProps } from "./types";

/**
 * Component that displays bank account details for a payee using PayeePaymentPrefDisplayCard.
 * Fetches payee data and displays payment preference information in a card format.
 */
export const PayeeDisplay: React.FC<PayeeDisplayProps> = ({
  payeeId,
  showDiligenceStatus = false,
}) => {
  const { data, loading, error } = useGetPayeeForBankAccountDetailsQuery({
    variables: { payeeId },
    context: clientContext(),
    skip: !payeeId,
    fetchPolicy: "cache-and-network",
  });

  useReportError(error ? formatError(error) : null);

  if (loading) {
    return <LoadingIndicator size="small" />;
  }

  if (error) {
    return <ErrorMessage> Error loading bank account details </ErrorMessage>;
  }

  const payeeData = data?.payee;

  if (!payeeData) {
    return <ErrorMessage> Payee information not available </ErrorMessage>;
  }

  const paymentPref = data?.payee?.paymentPref?.paymentPrefFull;

  if (!paymentPref) {
    return <ErrorMessage> No payment preference available </ErrorMessage>;
  }

  const iconType = getPaymentMethodIconType(paymentPref);

  const watchlistStatuses = data?.payee?.paymentPref?.watchlistStatus;
  const reviewTag = showDiligenceStatus
    ? getPayeeReviewTag(payeeData.status, watchlistStatuses)
    : null;

  return (
    <Card style={{ padding: 16 }}>
      <Flex flexDirection="column" gap="sm">
        <Flex alignItems="center" gap={8}>
          {payeeData?.name && <Text variant="strong-md">{payeeData.name}</Text>}
          {reviewTag === "pending-approval" && <Tag variant="warning">Pending review</Tag>}
          {reviewTag === "pending-screening" && (
            <Tag variant="warning">Pending screening review</Tag>
          )}
        </Flex>
        <div className={styles.childContainer}>
          {payeeData?.address && (
            <FormattedAddress
              address={{
                street: payeeData.address.street,
                street2: payeeData.address.street2,
                street3: payeeData.address.street3,
                city: payeeData.address.city,
                state: payeeData.address.state?.name,
                region: payeeData.address.region,
                country: payeeData.address.country?.name,
                postalCode: payeeData.address.postalCode,
              }}
              variant="secondary-sm"
            />
          )}
        </div>

        <PaymentPrefDisplayCardContainer iconType={iconType}>
          <BasePaymentPrefDisplay
            paymentPref={paymentPref}
            displayRevealDetails={true}
            renderDetails={() => <ExistingPaymentPrefDetails paymentPrefId={paymentPref.id!} />}
            defaultExpanded={false}
          />
        </PaymentPrefDisplayCardContainer>
      </Flex>
    </Card>
  );
};
